import { useEffect, useRef, useState } from "react";
import { IconBox } from "../IconBox";
import { css, keyframes, useTheme } from "@emotion/react";
import {
  useInsertMixContentViewMutation,
  usePopularContentsLast24HoursQuery,
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { zIndex } from "styles/zIndex";

type TrendingSearch = {
  id: number;
  term: string;
  rank: number;
  contentid: string;
};

// 텍스트만 애니메이션 되도록 새로운 애니메이션 추가
const textSlideAnimation = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

export default function TrendingContentsBanner() {
  const theme = useTheme();
  const [trendingSearches, setTrendingSearches] = useState<TrendingSearch[]>(
    [],
  );
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // 애니메이션 키를 변경하기 위한 state
  const [animationKey, setAnimationKey] = useState(0);
  // 컨테이너 참조 생성
  const containerRef = useRef<HTMLDivElement>(null);

  const { me } = useAuth();

  // contentView mutation 추가
  const [insertContentView] = useInsertMixContentViewMutation();

  // contentView 추가 핸들러
  const handleContentView = async (contentid: string) => {
    try {
      await insertContentView({
        variables: {
          object: {
            contentid,
            userid: me?.id,
          },
        },
      });
    } catch (error) {
      console.error("ContentView 추가 실패:", error);
    }
  };

  // 실시간 인기 콘텐츠 데이터 가져오기
  const {
    data: popularContentsData,
    loading,
    error,
  } = usePopularContentsLast24HoursQuery({
    variables: { limit: 10 },
    pollInterval: 600000, // 10분마다 데이터 갱신
  });

  // 인기 콘텐츠 데이터가 업데이트되면 trendingSearches 상태 업데이트
  useEffect(() => {
    if (error) {
      console.error("GraphQL 에러:", error);
      return;
    }

    if (loading) {
      return;
    }

    if (popularContentsData?.mix_content_popular_last_24h_view) {
      const updatedTrendingSearches =
        popularContentsData.mix_content_popular_last_24h_view.map(
          (item, index) => ({
            id: index + 1,
            term: item.content?.title || "",
            rank: index + 1,
            contentid: item.contentid || "",
          }),
        );
      setTrendingSearches(updatedTrendingSearches);
    } else {
      console.log("데이터가 없습니다:", popularContentsData);
    }
  }, [popularContentsData, loading, error]);

  // 3초마다 표시되는 인덱스를 회전시키는 함수
  useEffect(() => {
    if (!showAll) {
      intervalRef.current = setInterval(() => {
        setVisibleIndex(
          (prevIndex) => (prevIndex + 1) % trendingSearches.length,
        );
        // 애니메이션 키 변경으로 애니메이션 트리거
        setAnimationKey((prev) => prev + 1);
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [showAll, trendingSearches.length]);

  // 로딩 중이거나 에러가 있는 경우 초기 데이터 표시
  if (loading || error) {
    return null;
  }

  // 빈 배열 체크 및 기본값 제공
  if (trendingSearches.length === 0) {
    return null;
  }

  // 현재 표시할 항목
  const currentItem = trendingSearches[visibleIndex % trendingSearches.length]!;

  return (
    <div
      ref={containerRef}
      css={css`
        width: 100%;
        max-width: 276px;
        margin: 0;
        z-index: ${zIndex.subHeader};
        position: relative;

        display: block;
        @media (max-width: 768px) {
          display: none;
        }
      `}
      onMouseEnter={() => {
        setShowAll(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      }}
      onMouseLeave={() => {
        setShowAll(false);
        intervalRef.current = setInterval(() => {
          setVisibleIndex(
            (prevIndex) => (prevIndex + 1) % trendingSearches.length,
          );
          // 애니메이션 키 변경으로 애니메이션 트리거
          setAnimationKey((prev) => prev + 1);
        }, 3000);
      }}
    >
      {/* 단일 항목 - hover시 사라짐 */}
      {!showAll ? (
        <div
          css={css`
            padding: 6px 12px 3px 12px;
            display: flex;
            align-items: center;
            gap: 6px;
            background-color: ${theme.palette.grey[100]};
            color: ${theme.palette.text.primary};
            height: 32px;
            border-radius: ${theme.shape.radius.medium}px;
            overflow: hidden; /* 내부 애니메이션 요소가 넘치지 않도록 */
          `}
        >
          {/* 내부 콘텐츠를 감싸는 div - 애니메이션 적용 */}
          <div
            key={animationKey}
            css={css`
              display: flex;
              align-items: center;
              gap: 6px;
              width: 100%;
              animation: ${textSlideAnimation} 0.5s ease-out;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 15px;
                height: 24px;
                color: ${theme.palette.text.primary};
                ${theme.typography.body2};
              `}
            >
              {currentItem.rank}
            </div>
            <div
              css={css`
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                ${theme.typography.body3};
              `}
            >
              {currentItem.term}
            </div>
          </div>
        </div>
      ) : (
        // 호버시 모든 항목 표시
        <div
          css={css`
            position: absolute;
            top: -16px;
            left: 0;
            width: 100%;
            max-height: 357px;
            overflow-y: auto;
            background-color: ${theme.palette.grey[100]};
            border-radius: ${theme.shape.radius.medium}px;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            z-index: ${zIndex.categoryPicker};
          `}
        >
          {/* 실시간 인기 콘텐츠 헤더 */}
          <div
            css={css`
              display: flex;
              align-items: flex-end;
              gap: 4px;
              padding: 5px 12px;
              border-bottom: 1px solid ${theme.palette.grey[200]};
              color: ${theme.palette.primary.main};
              ${theme.typography.body2};
              font-weight: 700;
            `}
          >
            <IconBox
              name="Fire"
              size="small"
              css={css`
                color: ${theme.palette.primary.main};
              `}
            />
            실시간 인기 콘텐츠
          </div>

          {/* 나머지 항목들 (아래로 확장) */}
          <div>
            {trendingSearches
              .sort((a, b) => a.rank - b.rank)
              .map((search) => (
                <a
                  key={search.id}
                  href={
                    process.env.BUILD_TARGET === "extension"
                      ? `https://mix.day/contents/${search.contentid}`
                      : `/contents/${search.contentid}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => {
                    handleContentView(search.contentid);
                  }}
                  css={css`
                    padding: 4px 12px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: ${theme.palette.text.primary};
                    text-decoration: none;
                    &:hover {
                      background-color: ${theme.palette.grey[300]};
                    }
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      width: 15px;
                      height: 24px;
                      color: ${theme.palette.text.primary};
                      ${theme.typography.body2};
                    `}
                  >
                    {search.rank}
                  </div>
                  <div
                    css={css`
                      flex: 1;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                      ${theme.typography.body3};
                    `}
                  >
                    {search.term}
                  </div>
                </a>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
